/**
 * "You've used this month's messages", wherever that fact turns up.
 *
 * It began as a private component of the drawer, which is where a brewer first
 * meets it. Then the Feedback page needed the same thing: reading a photographed
 * scoresheet spends an assistant message (S2), so a brewer whose month is gone
 * can press "Add a competition sheet", have the pages stored, and get no
 * reading — and the honest answer to that is the same card, not a shorter
 * sentence that leaves out the date it comes back and the button that would fix
 * it. Two surfaces telling one fact two ways is how a product starts
 * contradicting itself about its own limits.
 *
 * It is a *card* rather than a toast because nothing here is an error: the pages
 * are stored, the scan is theirs, and the reading is one press away next month.
 */

import { CalendarClockIcon } from 'lucide-react';
import { usePlanLimit } from '@/components/plan-limit-dialog';
import { Button } from '@/components/ui/button';
import type { AiAllowance } from '@/lib/plan';
import { nextMonthLabel, PLAN_TIERS, planName } from '@/lib/plan';

/**
 * The month's allowance is gone.
 *
 * Distinct from the signed-out and unconfigured notices because the user has
 * done nothing wrong and nothing is broken: they have used a real thing up, it
 * comes back on a date, and there may or may not be a tier that gives them more
 * of it. Someone already on the top tier is told when it resets and is *not*
 * shown an upgrade button that would sell them what they have.
 */
export function AllowanceSpent({ allowance }: { readonly allowance: AiAllowance }) {
  const { show } = usePlanLimit();
  const top = allowance.plan === 'beerwright';

  return (
    <div
      data-testid="ai-allowance-spent"
      className="flex flex-col gap-2 rounded-lg border border-warning/40 bg-warning/5 px-3 py-3"
    >
      <p className="flex items-center gap-2 text-xs font-medium">
        <CalendarClockIcon className="size-4 text-warning" aria-hidden="true" />
        You’ve used this month’s {allowance.limit} messages
      </p>
      <p className="text-[11px] leading-snug text-muted-foreground">
        The {planName(allowance.plan)} plan includes {allowance.limit} assistant messages a month,
        and they come back on <strong>1 {nextMonthLabel(allowance.month)}</strong>. Everything else
        in Beerwright is untouched — the editor, the water tools and your recipes all still work.
      </p>
      {!top && (
        <>
          <p className="text-[11px] leading-snug text-muted-foreground">
            {planName('beerwright')} includes {PLAN_TIERS.beerwright.aiMonthlyMessages} messages a
            month.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="self-start"
            data-testid="ai-allowance-upgrade"
            onClick={() => show({ reason: 'ai-allowance' })}
          >
            See the plans
          </Button>
        </>
      )}
    </div>
  );
}
